/**
 * PostMeetingPage Component
 * TASK-397: Post-Meeting Tabbed Redesign
 *
 * Tabbed container: Summary, Tasks, Compliance, Follow-up.
 * Compliance is non-blocking (badge + soft warning on finish).
 * Fetches all data by meetingId from API — no location.state.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Shield,
  Mail,
  Dna,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../../utils/logger';
import { meetingApi, type MeetingDetails } from '../../../services/api/meetingApi';
import { meetingNotesService } from '../../../services/meetingNotesService';
import { postMeetingApi } from '../../../services/api/postMeetingApi';
import { clientService } from '../../../services/clientService';
import type { Task, LifeEvent, ComplianceDocument, DisclosureItem } from '../../../types/meeting';

import SummaryTab from './SummaryTab';
import { CAPTURE_SOURCE_LABELS } from '../meetingRouting';
import TasksTab from './TasksTab';
import MeetingComplianceTab from './ComplianceTab';
import FollowUpTab from './FollowUpTab';
import SessionExpiryBanner from '../SessionExpiryBanner';
import MeetingDnaReview from '../PostMeetingHub/MeetingDnaReview';
import MeetingRecordSummary from '../../canvas/widgets/MeetingRecordSummary';

// ============================================================================
// Types
// ============================================================================

type TabId = 'summary' | 'tasks' | 'compliance' | 'followup' | 'dna';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'summary', label: 'Summary', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'tasks', label: 'Tasks', icon: <CheckCircle2 className="w-4 h-4" /> },
  { id: 'compliance', label: 'Compliance', icon: <Shield className="w-4 h-4" /> },
  { id: 'followup', label: 'Follow-up', icon: <Mail className="w-4 h-4" /> },
  { id: 'dna', label: 'Client DNA', icon: <Dna className="w-4 h-4" /> },
];

// ============================================================================
// Component
// ============================================================================

/**
 * TASK-673: `embedded` renders the review inside a canvas tile (no full-screen wrapper, no
 * back-to-live route, no session banner — the WidgetContainer owns the frame), and
 * `meetingId` can be passed directly so it does not depend on being a routed page. This is
 * the last surface in the meeting flow that used to route out; it now stays on the canvas.
 */
const PostMeetingPage: React.FC<{ meetingId?: string; embedded?: boolean }> = ({
  meetingId: propMeetingId,
  embedded = false,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const meetingId = propMeetingId ?? paramId;
  const navigate = useNavigate();

  // Meeting data
  const [meeting, setMeeting] = useState<MeetingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Summary
  const [summary, setSummary] = useState('');
  const [finsaStatus, setFinsaStatus] = useState<'advisory_minutes' | 'business_notes' | 'no_documentation'>('no_documentation');
  const [requiresClientDelivery, setRequiresClientDelivery] = useState(false);

  // Tasks & life events
  const [tasks, setTasks] = useState<Task[]>([]);
  const [lifeEvents, setLifeEvents] = useState<LifeEvent[]>([]);

  // Compliance
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [disclosures, setDisclosures] = useState<DisclosureItem[]>([]);
  const [unsignedCount, setUnsignedCount] = useState(0);
  const [complianceConfidence, setComplianceConfidence] = useState<number | null>(null);

  // FINSA-derived insights surfaced from meetingNotesService when available.
  const [clientMood, setClientMood] = useState<string | null>(null);
  const [keyPointsCount, setKeyPointsCount] = useState<number | null>(null);

  // KYC
  const [hasPendingKYC, setHasPendingKYC] = useState(false);

  // Client email (fetched from clients API)
  const [clientEmail, setClientEmail] = useState('');

  // Layout
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [showFinishWarning, setShowFinishWarning] = useState(false);

  // Timer
  const [timer, setTimer] = useState('00:00');
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  // TASK-699 review: a meeting that ENDED has a duration, not a stopwatch. This surface ticked
  // `now - started_at` forever, so the review of a 2m26s meeting held on 2026-07-27 read
  // "2824:16" and grew by a second every second — a fabricated number on the record the RM
  // signs off. `ended_at` only became reliable with TASK-700's terminal transition.
  const [endedAt, setEndedAt] = useState<Date | null>(null);
  const isMountedRef = useRef(true);

  // Reset on mount as well as cleanup — in React StrictMode dev the first
  // mount's cleanup sets .current = false, and useRef persists across the
  // double-invocation, so without an explicit re-assign on mount every
  // `if (!isMountedRef.current) return` inside fetchData fires after the
  // first await and `finally` skips `setLoading(false)` → infinite loading.
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Live-ticking timer from started_at — but only while the meeting is actually running.
  useEffect(() => {
    if (!startedAt) return;
    const format = (seconds: number) => {
      const s = Math.max(0, seconds);
      return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60)
        .toString()
        .padStart(2, '0')}`;
    };
    if (endedAt) {
      // Finished: show how long it took, and stop. No interval to leak either.
      setTimer(format(Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000)));
      return;
    }
    const tick = () => setTimer(format(Math.floor((Date.now() - startedAt.getTime()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt, endedAt]);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  useEffect(() => {
    if (!meetingId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const details = await meetingApi.getMeetingDetails(meetingId);
        if (!isMountedRef.current) return;
        setMeeting(details);

        // Start live timer from started_at — and freeze it at ended_at when there is one.
        if (details.started_at) {
          setStartedAt(new Date(details.started_at));
        }
        setEndedAt(details.ended_at ? new Date(details.ended_at) : null);

        // Fetch meeting notes (summary, tasks, life events)
        try {
          // generateMeetingNotes expects a MeetingNotesRequest object, not a string
          const notes = await meetingNotesService.generateMeetingNotes({
            sessionId: meetingId,
            clientName: details.client_name,
          });
          if (!isMountedRef.current) return;
          if (notes.success) {
            setSummary(notes.summary || '');
            setTasks(notes.tasks || []);
            setLifeEvents(notes.lifeEvents || []);
            setFinsaStatus(notes.finsaStatus || 'no_documentation');
            setRequiresClientDelivery(notes.requiresClientDelivery || false);
            setClientMood(notes.clientMood ?? null);
            setKeyPointsCount(notes.keyPoints?.length ?? null);
          } else {
            const errorMsg = notes.error?.message || 'Failed to generate meeting notes';
            toast.error(errorMsg);
            logger.warn('[PostMeetingPage] Notes generation returned error:', notes.error);
          }
        } catch (notesErr) {
          logger.warn('[PostMeetingPage] Notes fetch failed:', notesErr);
          toast.error('Failed to load meeting notes');
        }

        // Check for KYC extraction (onboarding only)
        if (details.meeting_type === 'onboarding' && details.client_id) {
          try {
            const kycData = await postMeetingApi.getKYCPrefillData(details.client_id);
            if (kycData?.pending_extraction) {
              setHasPendingKYC(true);
            }
          } catch (kycErr) {
            logger.warn('[PostMeetingPage] KYC prefill check failed:', kycErr);
          }
        }

        // Fetch compliance documents and map to canonical ComplianceDocument type
        try {
          const complianceData = await postMeetingApi.getComplianceDocuments(meetingId);
          if (!isMountedRef.current) return;
          const mappedDocs: ComplianceDocument[] = complianceData.documents.map((d: Record<string, unknown>) => ({
            id: String(d.id || ''),
            title: String(d.title || ''),
            status: d.signatureStatus === 'signed' ? 'approved' as const : 'warning' as const,
            description: String(d.description || ''),
            documentType: (d.documentType || 'form') as ComplianceDocument['documentType'],
            signatureStatus: (d.signatureStatus || 'unsigned') as ComplianceDocument['signatureStatus'],
          }));
          setDocuments(mappedDocs);
          setDisclosures(complianceData.disclosures);
        } catch {
          logger.warn('[PostMeetingPage] Compliance documents fetch failed');
        }

        // Fetch compliance confidence score for the Follow-up tab stats panel.
        try {
          const status = await postMeetingApi.getComplianceStatus(meetingId);
          if (!isMountedRef.current) return;
          setComplianceConfidence(status.confidenceScore ?? null);
        } catch {
          logger.warn('[PostMeetingPage] Compliance status fetch failed');
        }

        // Fetch client email for follow-up tab
        if (details.client_id) {
          try {
            const client = await clientService.getClient(details.client_id);
            if (isMountedRef.current && client.email) setClientEmail(client.email);
          } catch {
            logger.warn('[PostMeetingPage] Client email fetch failed');
          }
        }
      } catch (err) {
        logger.error('[PostMeetingPage] Failed to load:', err);
        if (isMountedRef.current) setError('Meeting not found');
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    };

    fetchData();
  }, [meetingId]);

  // ============================================================================
  // Actions
  // ============================================================================

  const doFinish = useCallback(async () => {
    if (!meetingId) return;
    try {
      await meetingApi.completeMeeting(meetingId);
      toast.success('Meeting completed');
      navigate('/dashboard');
    } catch {
      toast.error('Failed to complete meeting');
    }
  }, [meetingId, navigate]);

  const handleFinish = useCallback(() => {
    if (unsignedCount > 0) {
      setShowFinishWarning(true);
    } else {
      doFinish();
    }
  }, [unsignedCount, doFinish]);

  const handleSendFollowUp = useCallback(async (email: string, subject: string, body: string) => {
    if (!meetingId) return;
    // H1: send the RM-reviewed subject/body verbatim (summary/actionItems kept as fallback).
    const res = await postMeetingApi.sendFollowUpEmail({
      sessionId: meetingId,
      recipientEmail: email,
      recipientName: meeting?.client_name || '',
      summary,
      actionItems: tasks.map((t) => t.title).filter(Boolean),
      subject,
      body,
    });
    // Report the TRUE outcome — never claim "sent" on a draft/failure (TASK-571).
    if (res.status === 'sent') {
      toast.success('Follow-up email sent');
    } else if (res.status === 'already_sent') {
      toast.info('Follow-up was already sent to this client.');
    } else {
      toast.error(`Email not sent (${res.status}). It was not delivered.`);
    }
  }, [meetingId, meeting, summary, tasks]);

  // Real CRM sync + toasts live in CRMSyncPanel (TASK-516). This runs after a
  // successful sync as an optional hook (e.g. refresh a CRM view) — no-op today.
  const handleSyncCRM = useCallback(() => {}, []);

  const handleRegenerate = useCallback(async () => {
    if (!meetingId) return;
    try {
      const notes = await meetingNotesService.generateMeetingNotes({
        sessionId: meetingId,
        clientName: meeting?.client_name,
      });
      if (notes.success) {
        setSummary(notes.summary || '');
        setTasks(notes.tasks || []);
        toast.success('Summary regenerated');
      }
    } catch {
      toast.error('Failed to regenerate summary');
    }
  }, [meetingId]);

  // ============================================================================
  // Render
  // ============================================================================

  if (!meetingId) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
      </div>
    );
  }

  const centreCls = embedded
    ? 'flex items-center justify-center h-64 bg-gray-50'
    : 'flex items-center justify-center h-screen bg-gray-50';

  if (loading) {
    return (
      <div className={centreCls}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading post-meeting data...</p>
        </div>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className={centreCls}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error || 'Meeting not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'flex flex-col h-[560px] bg-gray-100' : 'flex flex-col h-screen bg-gray-100'}>
      {!embedded && <SessionExpiryBanner />}
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          {!embedded && (
            <button
              onClick={() => navigate(`/meeting/${meetingId}/live`)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Post-Meeting Review</h1>
            <p className="text-xs text-gray-500">
              {meeting.client_name} — {timer}
            </p>
            {/* TASK-673 AC + the epic's Source-truth definition: say where these words came
                from, on the one screen where the RM reads them. Deep review #6 — provenance
                was carried on the meetings LIST and stopped there, so the review surface
                presented a 60-second dictation exactly like a verbatim bot transcript. */}
            <p data-testid="review-provenance" className="text-xs text-gray-600">
              {meeting.documentation_source === 'rm_dictation'
                ? 'Source: RM-dictated summary — not a verbatim transcript'
                : `Source: ${CAPTURE_SOURCE_LABELS[meeting.capture_source ?? 'none'] ?? meeting.capture_source}`}
            </p>
          </div>
        </div>
        <button
          onClick={handleFinish}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
        >
          Finish Meeting
        </button>
      </div>

      {/* TASK-699 review: the canonical record (TASK-705) belongs to the REVIEW SURFACE, not to
          one of its two entry points. It shipped on the canvas widget only, so an RM who opened
          /meeting/:id/review — a real route, and the one a bookmark or a deep link lands on —
          saw the tabs without the record: no provenance strip, no counts, no derived retention,
          and no way to tell a section that was empty from one that could not be read. */}
      {meetingId && (
        <div className="bg-white flex-shrink-0">
          <MeetingRecordSummary sessionId={meetingId} />
        </div>
      )}

      {/* Tabs */}
      {/* TASK-699 review: five tabs do not fit a 390px viewport, and the row had no overflow
          rule — so "Follow-up" and "Client DNA" were clipped off the right edge with no way to
          reach them, and the whole PAGE scrolled sideways instead. Scroll the strip, not the
          document. */}
      <div className="flex overflow-x-auto border-b border-gray-200 bg-white px-2 flex-shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.icon}
            {tab.label}
            {/* Compliance badge */}
            {tab.id === 'compliance' && unsignedCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full">
                {unsignedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden bg-white">
        {activeTab === 'summary' && (
          <SummaryTab
            sessionId={meetingId}
            clientId={meeting.client_id}
            summary={summary}
            timer={timer}
            finsaStatus={finsaStatus}
            requiresClientDelivery={requiresClientDelivery}
            meetingType={meeting.meeting_type}
            hasPendingKYC={hasPendingKYC}
            onSummaryChange={setSummary}
            onRegenerate={handleRegenerate}
            onKYCApproved={() => setHasPendingKYC(false)}
          />
        )}
        {activeTab === 'tasks' && (
          <TasksTab
            tasks={tasks}
            lifeEvents={lifeEvents}
            onAddTask={(task) => setTasks((prev) => [...prev, task])}
            onAddLifeEvent={(event) => setLifeEvents((prev) => [...prev, event])}
            onEditLifeEvent={(event) =>
              setLifeEvents((prev) => prev.map((e) => (e.id === event.id ? event : e)))
            }
            onDeleteLifeEvent={(id) =>
              setLifeEvents((prev) => prev.filter((e) => e.id !== id))
            }
          />
        )}
        {activeTab === 'compliance' && (
          <MeetingComplianceTab
            sessionId={meetingId}
            documents={documents}
            disclosures={disclosures}
            onDocumentsChange={setDocuments}
            onDisclosuresChange={setDisclosures}
            onUnsignedCountChange={setUnsignedCount}
          />
        )}
        {activeTab === 'followup' && (
          <FollowUpTab
            sessionId={meetingId}
            clientName={meeting.client_name}
            clientEmail={clientEmail}
            meetingSummary={summary}
            durationLabel={timer}
            // confidenceScore is normalised 0-1 from the backend; multiply for
            // percent display. If the backend ever switches scale, adjust here.
            complianceScorePct={
              complianceConfidence == null ? null : Math.round(complianceConfidence * 100)
            }
            clientMood={clientMood}
            tasksCount={tasks.length}
            keyTopicsCount={keyPointsCount}
            tasks={tasks}
            meetingType={meeting.meeting_type}
            onSendFollowUp={handleSendFollowUp}
            onSyncCRM={handleSyncCRM}
            onFinish={doFinish}
          />
        )}
        {activeTab === 'dna' && (
          meeting.client_id ? (
            <MeetingDnaReview
              sessionId={meeting.session_id}
              clientId={meeting.client_id}
              emptyFallback={
                <p className="p-4 text-sm text-gray-500">No Client DNA was derived from this meeting.</p>
              }
            />
          ) : (
            <p className="p-4 text-sm text-gray-500">No client linked to this meeting.</p>
          )
        )}
      </div>

      {/* Finish warning dialog */}
      {showFinishWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                Compliance Items Pending
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {unsignedCount} document{unsignedCount !== 1 ? 's are' : ' is'} still unsigned.
              You can complete these later from the client profile.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowFinishWarning(false);
                  setActiveTab('compliance');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Go to Compliance
              </button>
              <button
                onClick={() => {
                  setShowFinishWarning(false);
                  doFinish();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                Finish Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostMeetingPage;
